import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// mui components
import { Box, Button, Grid, Typography, styled } from '@mui/material';
// components
import Comments from '../../comments';
import Reply from '../../reply';
import CommentsCount from './components/comments-count';
import LikesCount from './components/likes-count';
import PostAvatar from './components/post-avatar';
// custom hooks'
import useToast from 'hooks/useToast';
import { useActionsPopper } from './hooks/use-actions-popper';
// api
import { IUpdatePostBody, PostVisibilityEnum } from 'redux-store/api/activity-feed';
// helpers'
import { isContentEmpty } from 'utils/helpers';
// icons'
import DotIcon from 'assets/icons/buttons/dot.svg';
import LikeIcon from 'assets/icons/buttons/like-icon.svg';
import ReplyIcon from 'assets/icons/buttons/reply-icon.svg';
import UnlikeIcon from 'assets/icons/buttons/unlike-icon.svg';
// utils'
import { createContent } from 'utils/helpers';
// constants'
// styles'
import { IPost } from '_interfaces/post.interface';
import { IPostComment } from '_interfaces/postComment.interface';
import { postCommentsAtom, selectedCompanyAtom, selectedProjectAtom } from 'atoms';
import { ICustomMentionData } from 'components/inputs/draft-js-input';
import UploadedAudioContent from 'components/uploaded-audio-content';
import UploadedFiles from 'components/uploaded-files';
import { isAudio, isVideo } from 'components/uploaded-images/helpers';
import { UploadedImagesWithStatus } from 'components/uploaded-images/uploaded-images-with-status';
import { getDownloadURL, ref } from 'firebase/storage';
import { useAtomValue } from 'jotai';
import { useGetMeQuery } from 'redux-store/api/auth';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { IUploadedFilesData } from 'redux-store/types';
import { storage } from 'utils/firebase';
import { getMimeTypeFromFileName } from 'utils/helpers/fileCabinetHelpers';
import { highlightSearchMatchesInHtml } from 'utils/searchHighlight';
import { RichTextContent } from 'widgets/side-panel/sections/activity-feed/components/rich-text-content';
import Post from '../../post';
import { postItemStyles } from './styles/styles';

dayjs.extend(LocalizedFormat);

export interface IPostItemProps {
  post: IPost;
  placeOfUse: 'activity_feed' | 'post_modal';
  projectId?: number;
  mentionOptions: ICustomMentionData[];
  searchQuery: string;
  onCreateComment: (body: Partial<IPostComment>, parentPath?: string) => void;
  onUpdateComment: (body: Partial<IPostComment>) => void;
  onUpdatePost?: (body: Partial<IPost>) => void;
  onDeletePost?: (id: string) => void;
  onDeleteComment?: (id: string) => void;
  onTogglePostLike: () => void;
}

const StyledSpan = styled('span')({
  width: '3px',
  height: '3px',
  backgroundColor: '#D4D6E0',
  borderRadius: '50%',
});

const UserPost: FC<IPostItemProps> = ({
  post,
  placeOfUse,
  projectId,
  mentionOptions,
  searchQuery,
  onCreateComment,
  onUpdateComment,
  onUpdatePost,
  onDeletePost,
  onDeleteComment,
  onTogglePostLike,
}) => {
  const showToast = useToast();

  const replyRef = useRef<HTMLDivElement>(null);
  const [replyOpen, setReplyOpen] = useState(placeOfUse === 'post_modal');
  const [commentsOpen, setCommentsOpen] = useState(true);

  const comments = useAtomValue(postCommentsAtom(`${post.parentPath}/${post.id}`));

  const selectedProject = useAtomValue(selectedProjectAtom);
  const selectedCompany = useAtomValue(selectedCompanyAtom);
  const { data: userMe } = useGetMeQuery();
  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();
  const [isEditing, setIsEditing] = useState(false);
  const [videoThumbnails, setVideoThumbnails] = useState<Map<string, string>>(new Map());

  const { popperButton } = useActionsPopper({
    placeOfUse,
    post,
    projectId: null,
    onEdit: () => {
      setIsEditing(true);
    },
    onDelete: onDeletePost,
  });

  const handleLikeUnlike = async () => {
    onTogglePostLike();
  };

  const handleToggleReply = () => {
    setReplyOpen((prev) => !prev);
  };

  // Convert storage paths to download URLs for transcoded files
  useEffect(() => {
    if (!post.media) return;

    const convertPathsToURLs = async () => {
      const pathToUrlMap = new Map<string, string>();

      for (const mediaItem of post.media || []) {
        if (mediaItem.transcoded?.thumbnail) {
          // Check if it's a storage path (doesn't start with http)
          if (!mediaItem.transcoded.thumbnail.startsWith('http')) {
            const storageRef = ref(storage, mediaItem.transcoded.thumbnail);

            try {
              const downloadURL = await getDownloadURL(storageRef);
              pathToUrlMap.set(mediaItem.transcoded.thumbnail, downloadURL);
            } catch (error) {
              console.error('Error fetching download URL:', error);
            }
          }
        }
      }

      if (pathToUrlMap.size > 0) {
        setVideoThumbnails((prev) => {
          const updated = new Map(prev);
          pathToUrlMap.forEach((url, path) => {
            updated.set(path, url);
          });
          return updated;
        });
      }
    };

    convertPathsToURLs();
  }, [post.media]);

  const handleOpenReply = (open: boolean) => {
    setReplyOpen(open);
  };

  const handleOpenTextEditor = (open: boolean) => {
    setIsEditing(open);
  };

  const handleClickOnComments = useCallback(() => {
    if (comments.length > 0) {
      if (commentsOpen) {
        setCommentsOpen(false);
        return;
      }
      setCommentsOpen(true);
    }
  }, [comments, placeOfUse, commentsOpen, post.id]);

  const handleShare = async () => {
    await navigator.clipboard.writeText(
      `${window.location.href}${placeOfUse === 'activity_feed' ? `?postId=${post.id}` : ''}`
    );
    showToast({ type: 'success', message: 'Post link has been copied to clipboard' });
  };

  const content = useMemo(() => {
    const baseContent = createContent(post.message, post.mentions, post.hashtags);
    return highlightSearchMatchesInHtml(baseContent, searchQuery);
  }, [post.message, post.mentions, post.hashtags, searchQuery]);

  const postDataForUpdate: IUpdatePostBody = {
    projectId: null,
    id: Number(post.id),
    content: post.message,
    postVisibility: PostVisibilityEnum.EVERYONE,
    // uploads: post.media,
  };

  const isDescriptionEditable =
    post?.createdBy?.userId?.toString() === userMe?.id?.toString() ||
    (userPermissions?.ACTIVITY_FEED_ALL_POSTS?.canUpdate &&
      userPermissions?.ENABLE_ACTIVITY_FEED?.canUpdate);

  const renderPostInfo = (
    <Grid sx={postItemStyles.postInfoContainer}>
      <LikesCount post={post} />
      <StyledSpan />
      <CommentsCount post={post} handleClickOnComments={handleClickOnComments} />
    </Grid>
  );

  const renderPostActions = (
    <Grid sx={postItemStyles.likeReplyContainer}>
      <Button
        size="small"
        onClick={handleLikeUnlike}
        startIcon={post?.likes?.includes(userMe?.id?.toString()) ? <UnlikeIcon /> : <LikeIcon />}
        sx={postItemStyles.actionBtn}
      >
        {post?.likes?.includes(userMe?.id?.toString()) ? 'Liked' : 'Like'}
      </Button>
      <Button
        onClick={handleToggleReply}
        size="small"
        startIcon={<ReplyIcon />}
        sx={postItemStyles.actionBtn}
      >
        Reply
      </Button>
      {/* <Button
        onClick={handleShare}
        size="small"
        startIcon={<ShareIcon />}
        sx={postItemStyles.actionBtn}
      >
        Share
      </Button> */}
    </Grid>
  );

  const renderTextContent =
    content && !isContentEmpty(content) ? (
      <RichTextContent
        projectId={projectId}
        content={content}
        sx={postItemStyles.postContentContainer}
      />
    ) : null;

  const uploadedImages = useMemo(
    () =>
      post.media
        ?.filter((mediaItem) => {
          const mimeType = getMimeTypeFromFileName(mediaItem.name);
          return !isAudio(mimeType); // Include both images and videos
        })
        .map((mediaItem, index) => {
          const mimeType = getMimeTypeFromFileName(mediaItem.name);

          // Get thumbnail URL for videos
          let thumbnailUrl: string | undefined = undefined;
          if (isVideo(mimeType) && mediaItem.transcoded?.thumbnail) {
            // Check if it's already a URL or a storage path
            if (mediaItem.transcoded.thumbnail.startsWith('http')) {
              thumbnailUrl = mediaItem.transcoded.thumbnail;
            } else {
              // It's a storage path, check if we've converted it to a URL
              thumbnailUrl = videoThumbnails.get(mediaItem.transcoded.thumbnail);
            }
          }

          const item = {
            id: mediaItem.url || `firebase-media-${index}`,
            url: mediaItem.url,
            name: mediaItem.name,
            mimeType,
            options: {
              previewUrl: thumbnailUrl, // Use fetched thumbnail for video preview
            },
          };
          return item;
        }) || [],
    [post.media, videoThumbnails]
  );

  return (
    <Grid sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
      <Grid sx={postItemStyles.container}>
        <Grid sx={postItemStyles.headerContainer}>
          <Grid sx={postItemStyles.profileContainer}>
            <PostAvatar post={post} />
            <Grid sx={postItemStyles.userInfoContainer}>
              <Typography
                sx={postItemStyles.userName}
              >{`${post.createdBy.displayName}`}</Typography>

              {post.createdBy?.role && (
                <Box sx={postItemStyles.usersIconBox}>
                  {/* <PostAudience post={post} projectId={selectedProject.id} /> */}

                  <DotIcon />
                  <Typography sx={postItemStyles.userRole}>{post.createdBy.role}</Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          <Grid sx={postItemStyles.postDateContainer}>
            <Typography
              sx={{
                ...postItemStyles.date,
                cursor: placeOfUse === 'activity_feed' ? 'pointer' : 'auto',
              }}
            >
              {`${dayjs(post?.createdAt).format('ll')} / ${dayjs(post?.createdAt).format('LT')}`}
            </Typography>
            {popperButton}
          </Grid>
        </Grid>

        {isEditing ? (
          <Post
            isExpanded={true}
            actionType="UPDATE_POST"
            projectId={parseInt(post.projectId)}
            companyId={post.companyId}
            mentionOptions={mentionOptions}
            editablePost={post}
            setOpen={setIsEditing}
            onUpdatePost={onUpdatePost}
          />
        ) : (
          <Grid sx={postItemStyles.container}>
            {renderTextContent}

            <UploadedImagesWithStatus
              type="activity_feed"
              visibleImagesCount={5}
              uploads={uploadedImages}
              containerStyles={{ display: 'flex', width: '100%', height: '200px' }}
              editablePostBody={postDataForUpdate}
              isDescriptionEdtable={isDescriptionEditable}
            />

            <UploadedAudioContent
              uploadedMultimedia={
                post.media
                  ?.filter((mediaItem) => {
                    const mimeType = getMimeTypeFromFileName(mediaItem.name);
                    return isAudio(mimeType); // Only audio, not videos
                  })
                  .map(
                    (mediaItem, index) =>
                      ({
                        id: mediaItem.url || `firebase-media-${index}`,
                        url: mediaItem.url,
                        name: mediaItem.name,
                        mimeType: getMimeTypeFromFileName(mediaItem.name),
                      }) as IUploadedFilesData
                  ) || []
              }
            />

            <UploadedFiles
              type="activity_feed"
              uploads={
                post.media
                  ?.filter((mediaItem) => {
                    const mimeType = getMimeTypeFromFileName(mediaItem.name);
                    return !isVideo(mimeType) && !isAudio(mimeType) && mediaItem.type === 'file';
                  })
                  .map((mediaItem, index) => ({
                    id: mediaItem.url || `firebase-media-${index}`,
                    url: mediaItem.url,
                    name: mediaItem.name,
                    path: mediaItem.url, // Use URL as path for backward compatibility
                    mimeType: getMimeTypeFromFileName(mediaItem.name),
                    description: mediaItem.description || null,
                    location: null,
                    timestamp: null,
                    options: null,
                    status: 'PUBLISHED' as any, // Assume published for existing posts
                  })) || []
              }
            />

            <Grid sx={postItemStyles.actionsContainer}>
              {userMe ? renderPostActions : <Box />}
              {renderPostInfo}
            </Grid>

            {replyOpen ? (
              <Reply
                projectId={parseInt(post.projectId)}
                companyId={post.companyId}
                // setCommentsOpen={setCommentsOpen}
                mentionOptions={mentionOptions}
                setOpen={handleOpenReply}
                // addLocal={placeOfUse === 'activity_feed'}
                replyRef={replyRef}
                onCreateComment={onCreateComment}
                onUpdateComment={onUpdateComment}
              />
            ) : null}

            {commentsOpen && comments.length > 0 ? (
              <Comments
                placeOfUse={placeOfUse}
                comments={comments}
                searchQuery={searchQuery}
                mentionOptions={mentionOptions}
                // commentsCount={comments.length}
              />
            ) : null}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default memo(UserPost);
